import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, LinearProgress, CircularProgress } from '@mui/material';
import { Storage as StorageIcon } from '@mui/icons-material';
import PageHeader from '../../components/ui/PageHeader';
import analyticsService from '../../services/analyticsService';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import usePageTitle from '../../hooks/usePageTitle';

interface StorageUsage {
  used: number;
  total: number;
  available: number;
  percentage: number;
  fileCount: number;
  byFileType: {
    type: string;
    size: number;
    count: number;
    percentage: number;
  }[];
  recentGrowth: {
    date: string;
    size: number;
  }[];
}

const StorageAnalytics: React.FC = () => {
  usePageTitle('Storage Analytics');
  const [storageData, setStorageData] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getStorageAnalytics();
        setStorageData(data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load storage analytics');
        console.error('Storage analytics error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  // Guard against missing data
  if (!storageData) {
    return null;
  }

  // Compute dynamic values
  const usedGB = storageData.used ? storageData.used / (1024 * 1024 * 1024) : 0;
  const totalGB = storageData.total ? storageData.total / (1024 * 1024 * 1024) : 0;
  const availableGB = storageData.available ? storageData.available / (1024 * 1024 * 1024) : (totalGB - usedGB);
  const usedPercentage = storageData.percentage || (totalGB > 0 ? (usedGB / totalGB) * 100 : 0);
  const growthData = (storageData.recentGrowth ?? []).map((item: { date: string; size: number }) => ({
    date: item.date,
    size: item.size
  }));

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      <PageHeader
        title="Storage Analytics"
        icon={<StorageIcon fontSize="large" color="primary" />}
        description="Monitor and analyze your storage usage"
      />
      
      <Grid container spacing={3}>
        {/* Overall storage card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Storage Overview
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ flexGrow: 1, mr: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={usedPercentage} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      backgroundColor: usedPercentage > 90 ? '#d32f2f' : usedPercentage > 75 ? '#ff9800' : '#4caf50',
                    }
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {usedPercentage.toFixed(1)}% used
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body1" fontWeight="bold">
                  {usedGB.toFixed(2)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Used Storage
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body1" fontWeight="bold">
                  {totalGB.toFixed(2)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Quota
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body1" fontWeight="bold">
                  {availableGB.toFixed(2)} GB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </Grid>
            </Grid>
            {storageData.fileCount !== undefined && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Files: <strong>{storageData.fileCount.toLocaleString()}</strong>
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Storage by file type */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Storage by File Type
            </Typography>
            <Box sx={{ mt: 2 }}>
              {(storageData.byFileType ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No files uploaded yet
                </Typography>
              ) : (
                (storageData.byFileType ?? []).map((item, index) => {
                  const sizeGB = item.size / (1024 * 1024 * 1024);
                  const sizeMB = item.size / (1024 * 1024);
                  const displaySize = sizeGB >= 1 ? `${sizeGB.toFixed(2)} GB` : `${sizeMB.toFixed(2)} MB`;
                  
                  // Generate different colors for different file types
                  const colors = ['#3f51b5', '#f50057', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#795548'];
                  const color = colors[index % colors.length];
                  
                  return (
                    <Box key={item.type} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" textTransform="capitalize">
                          {item.type} ({item.count || 0} files)
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {displaySize} ({item.percentage.toFixed(1)}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: color
                          }
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Recent growth */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Storage Growth (Last 30 Days)
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              {growthData.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    No growth data available yet
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={growthData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const gb = value / (1024 * 1024 * 1024);
                        return gb >= 1 ? `${gb.toFixed(1)}GB` : `${(value / (1024 * 1024)).toFixed(0)}MB`;
                      }}
                    />
                    <Tooltip 
                      formatter={(value: number) => {
                        const gb = value / (1024 * 1024 * 1024);
                        const mb = value / (1024 * 1024);
                        return gb >= 1 ? `${gb.toFixed(2)} GB` : `${mb.toFixed(2)} MB`;
                      }}
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString();
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="size" 
                      stroke="#3f51b5" 
                      strokeWidth={2}
                      dot={{ fill: '#3f51b5', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StorageAnalytics;
export {}; 